import { Aide } from '@/components/ui/aide';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Pencil, Phone } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import useHasOwnStations from '../hooks/useHasOwnStations';
import { LEXIQUE } from '../lib/lexique';
import { TRUCK_STATUS_META, effectiveTruckStatus, truckStatusLabel } from '../lib/truckStatus';
import { formatDateTime } from '../lib/utils';
import { libelle, nomPays, texteSur } from '../lib/libelles';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { RecordSplit } from '../components/ui/page-layouts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useTruck, useTruckProfile } from '../components/truck/useTruck';
import TruckFormDialog from '../components/truck/TruckFormDialog';
import TruckImmobilizationActions from '../components/truck/TruckImmobilizationActions';
import TruckDocumentsPanel from '../components/truck/TruckDocumentsPanel';
import TruckTripsTable from '../components/truck/TruckTripsTable';
import TruckObservationsTab from '../components/truck/TruckObservationsTab';
import TruckRentalTab from '../components/truck/TruckRentalTab';
import TruckProfitTab from '../components/truck/TruckProfitTab';
import TruckStationRefuelsTab from '../components/truck/TruckStationRefuelsTab';
import TruckExpensesTab from '../components/truck/TruckExpensesTab';
import TruckPositionCard from '../components/truck/TruckPositionCard';
import { accorde } from '../lib/formats';
import { TruckMaintenancePanel } from './TruckMaintenancePage';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

/**
 * La fiche d'un camion: `/trucks/:truckId`.
 *
 * Elle remplace le tiroir de détails et le dialogue « Profil camion » de
 * l'écran Camions, ainsi que la fiche maintenance à part. Un camion se lit
 * enfin d'un bloc: ses deux plaques et ses deux châssis, son statut, ses
 * papiers, ses trajets et son atelier.
 *
 * - La base vient de `trucksAPI.getById`, qui retombe sur le cache local hors
 *   ligne. Le bandeau (`/profile`) est en ligne seulement et peut manquer sans
 *   empêcher la fiche de s'ouvrir.
 * - Chaque onglet n'existe que si le module de la licence le permet, et ne se
 *   charge qu'à son ouverture (le contenu d'un onglet fermé n'est pas monté).
 * - L'onglet actif vit dans l'adresse (`?tab=`), pour qu'un lien mène au bon
 *   endroit — une alerte papier ouvre directement les papiers.
 * - Montants des trajets réservés à la direction (`canSeeMoney`); les coûts
 *   d'atelier de l'onglet Maintenance restent visibles comme avant.
 */

export const TRUCK_TABS = [
  { value: 'apercu', label: 'Vue d’ensemble', module: null },
  { value: 'documents', label: 'Papiers', module: 'maintenance' },
  { value: 'trajets', label: 'Trajets', module: 'trips' },
  { value: 'maintenance', label: 'Entretien et pannes', module: 'maintenance' },
  { value: 'frais', label: 'Frais', module: 'finance_cockpit' },
  // Lots 017 à 019: chaque onglet vit dans son composant (components/truck/).
  { value: 'observations', label: 'Observations', module: null },
  { value: 'location', label: 'Location', module: 'truck_rentals' },
  { value: 'pleins', label: 'Pleins à la station', module: 'stations', stationsOnly: true },
  { value: 'rentabilite', label: 'Rentabilité', module: 'finance_cockpit', moneyOnly: true },
];

const DEFAULT_TAB = 'apercu';

const severityVariant = (severity) =>
  severity === 'critical' ? 'destructive' : severity === 'info' ? 'info' : 'warning';

function Line({ label, value, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className={`truncate text-right font-medium text-foreground ${mono ? 'font-mono' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

export default function TruckDetailPage() {
  const { truckId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasModule, canSeeMoney } = useAuth();
  const aDesStations = useHasOwnStations();

  const { truck, loading, error, reload } = useTruck(truckId);
  const { profile, unavailable: profileUnavailable, reload: reloadProfile } =
    useTruckProfile(truckId);
  const [editOpen, setEditOpen] = useState(false);

  const backTo = location.state?.from || '/trucks';
  const backLabel = backTo.startsWith('/maintenance')
    ? 'Retour à la maintenance'
    : backTo.startsWith('/trucks')
      ? 'Retour aux camions'
      : 'Retour';

  const tabs = TRUCK_TABS.filter(
    (tab) =>
      (!tab.module || hasModule(tab.module)) &&
      (!tab.moneyOnly || canSeeMoney) &&
      (!tab.stationsOnly || aDesStations),
  );
  const requestedTab = searchParams.get('tab');
  const activeTab = tabs.some((tab) => tab.value === requestedTab) ? requestedTab : DEFAULT_TAB;

  const selectTab = (value) => {
    // L'état de navigation porte le chemin du retour: on le garde.
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('tab', value);
        return next;
      },
      { replace: true, state: location.state },
    );
  };

  const refreshAll = async () => {
    await Promise.all([reload(), reloadProfile()]);
  };

  // Un frais, un trajet, un entretien ou un contrat enregistré ailleurs change
  // la fiche: elle se recharge d'elle-même.
  useRafraichirSur(
    ['camions', 'depenses', 'finance', 'trajets', 'maintenance', 'locations', 'documents'],
    refreshAll,
  );

  if (loading && !truck) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (!truck) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <PageHeader eyebrow="Parc" title="Fiche camion" backTo={backTo} backLabel={backLabel} />
        <EmptyState
          variant="error"
          title={error ? 'La fiche n’a pas pu être chargée' : 'Camion introuvable'}
          description="Ce camion n’existe pas ou n’appartient pas à votre flotte. Revenez à la liste des camions pour en choisir un autre."
          actionLabel="Voir les camions"
          onAction={() => navigate('/trucks')}
        />
      </div>
    );
  }

  const displayStatus = profile?.display_status || effectiveTruckStatus(truck);
  const statusMeta = TRUCK_STATUS_META[displayStatus] || { tone: 'muted' };
  const immobilization = profile?.immobilization || null;
  const documentsSummary = profile?.documents || null;
  const alerts = Array.isArray(profile?.alerts) ? profile.alerts : [];
  const currentTrip = profile?.current_trip || null;
  const counts = profile?.counts || null;
  const hasDocumentsTab = tabs.some((tab) => tab.value === 'documents');
  const sharedCount = Array.isArray(truck.organization_ids) ? truck.organization_ids.length : 0;

  const ouvrirPapiers = hasDocumentsTab ? () => selectTab('documents') : undefined;
  const statsEntete = [
    { label: 'Trajets', valeur: counts?.trips_total ?? null, aide: 'Depuis l’enregistrement' },
    {
      label: 'En cours',
      valeur: counts?.trips_active ?? null,
      aide: 'Trajets en cours',
    },
    ...(documentsSummary
      ? [
          {
            label: 'Papiers expirés',
            valeur: documentsSummary.expired ?? 0,
            ton: documentsSummary.expired ? 'critique' : 'positif',
            onClick: ouvrirPapiers,
          },
          {
            label: 'À renouveler',
            valeur: documentsSummary.expiring ?? 0,
            ton: documentsSummary.expiring ? 'attention' : 'positif',
            onClick: ouvrirPapiers,
          },
        ]
      : []),
  ];
  const alertesEntete = [
    immobilization
      ? {
          niveau: 'critique',
          court: 'Immobilisé',
          texte: `Immobilisé : ${texteSur(immobilization.reason, 'motif non précisé')}${
            immobilization.started_at ? ` · depuis le ${formatDateTime(immobilization.started_at)}` : ''
          }`,
        }
      : null,
    alerts.length > 0
      ? {
          niveau: 'attention',
          nombre: alerts.length,
          court: alerts.length > 1 ? 'alertes ouvertes' : 'alerte ouverte',
          texte: `${accorde(alerts.length, 'alerte ouverte', 'alertes ouvertes')} sur ce camion. Le détail est dans l’aperçu.`,
          action: activeTab !== 'apercu' ? { label: 'Voir l’aperçu', onClick: () => selectTab('apercu') } : undefined,
        }
      : null,
    currentTrip
      ? {
          niveau: 'info',
          court: 'Trajet en cours',
          texte: `Trajet en cours · ${libelle('tripStatus', currentTrip.status)}`,
          action: {
            label: 'Ouvrir le trajet',
            to: `/trips/${currentTrip.id}`,
          },
        }
      : null,
    profileUnavailable
      ? {
          niveau: 'info',
          court: 'Résumé indisponible',
          texte:
            'Résumé du camion indisponible pour le moment : l’identité affichée est celle de votre dernière consultation.',
        }
      : null,
  ].filter(Boolean);

  const aside = (
    <>
      <section className="space-y-3 rounded-xl border border-border bg-card p-4" data-testid="truck-identity">
        <h2 className="text-base">Identité</h2>
        <dl className="space-y-2 text-sm">
          <Line label={LEXIQUE.plaqueTracteur} value={truck.matricule} mono />
          <Line label={LEXIQUE.plaqueCiterne} value={truck.tank_number} mono />
          <Line label={LEXIQUE.chassisTracteur} value={truck.tractor_vin} mono />
          <Line label={LEXIQUE.chassisCiterne} value={truck.tank_vin} mono />
          <Line label="Marque" value={truck.brand} />
          <Line label="Année" value={truck.year} />
          <Line
            label="Capacité"
            value={
              truck.capacity_liters
                ? `${Number(truck.capacity_liters).toLocaleString('fr-FR')} L`
                : null
            }
          />
          <Line
            label="État technique"
            value={truck.technical_status ? libelle('technicalStatus', truck.technical_status) : null}
          />
        </dl>
        {sharedCount > 1 && (
          <p className="flex items-center gap-1 rounded-md border border-flame/30 bg-flame/10 px-3 py-2 text-xs text-flame-ink">
            Partagé avec {sharedCount} sociétés.
            <Aide titre="Camion partagé">
              Les numéros physiques déjà remplis ne se
              modifient que par la société qui a créé le camion; un numéro vide peut être
              complété par chacune.
            </Aide>
          </p>
        )}
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <h2 className="text-base">Disponibilité</h2>
        {immobilization ? (
          <div className="space-y-1 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
            <p className="text-sm font-semibold text-destructive">{texteSur(immobilization.reason, 'Motif non précisé')}</p>
            <Aide titre="Camion immobilisé">
              La répartition refusera toute affectation tant que l’immobilisation n’est pas
              levée.
            </Aide>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Statut : <span className="font-medium text-foreground">{truckStatusLabel(displayStatus)}</span>
          </p>
        )}
        <TruckImmobilizationActions
          truck={truck}
          immobilization={immobilization}
          onChanged={refreshAll}
        />
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <h2 className="text-base">Propriété et chauffeur</h2>
        <dl className="space-y-2 text-sm">
          <Line
            label="Propriété"
            value={truck.ownership_type === 'LOUE' ? 'Camion loué' : 'Camion propre'}
          />
          {truck.ownership_type === 'LOUE' && (
            <>
              <Line label="Propriétaire" value={truck.owner_name} />
              <Line label="Tél. propriétaire" value={truck.owner_phone} />
            </>
          )}
          {truck.company_name && <Line label="Société" value={truck.company_name} />}
          <Line label="Chauffeur" value={truck.driver_full_name} />
        </dl>
        {truck.driver_phone && (
          <Button variant="outline" size="sm" className="w-full" asChild>
            <a href={`tel:${truck.driver_phone}`}>
              <Phone className="h-4 w-4" aria-hidden="true" />
              {truck.driver_phone}
            </a>
          </Button>
        )}
      </section>
    </>
  );

  const overview = profileUnavailable && !profile ? (
    <EmptyState
      variant="empty"
      icon={AlertTriangle}
      title="Vue d’ensemble indisponible"
      description="Les compteurs, alertes et papiers à échéance ne peuvent pas être calculés pour le moment. L’identité et les onglets restent consultables; la vue d’ensemble revient dès que possible."
      actionLabel="Réessayer"
      onAction={reloadProfile}
    />
  ) : (
    <div className="space-y-4" data-testid="truck-overview">
      {hasModule('gps_tracking') && <TruckPositionCard truck={truck} />}

      {currentTrip && (
        <section className="space-y-1 rounded-xl border border-border bg-card p-4">
          <h2 className="text-base">Trajet en cours</h2>
          <p className="text-sm text-muted-foreground">
            {nomPays(currentTrip.departure_country)} → {nomPays(currentTrip.destination_country)} ·{' '}
            {libelle('tripStatus', currentTrip.status)}
          </p>
          <Button variant="link" size="sm" className="h-auto p-0" asChild>
            <Link
              to={`/trips/${currentTrip.id}`}
              state={{ from: `${location.pathname}${location.search}` }}
            >
              Ouvrir le trajet
            </Link>
          </Button>
        </section>
      )}

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-base">Alertes ouvertes</h2>
        {alerts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune alerte ouverte sur ce camion.</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((alert) => (
              <li key={alert.id} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{texteSur(alert.title, 'Alerte')}</p>
                  {alert.message && <p className="text-muted-foreground">{texteSur(alert.message, '')}</p>}
                </div>
                <Badge variant={severityVariant(alert.severity)}>
                  {alert.severity === 'critical' ? 'Critique' : alert.severity === 'info' ? 'Information' : 'Attention'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );

  const main = (
    <Tabs value={activeTab} onValueChange={selectTab} className="space-y-3">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
        {tabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value} data-testid={`truck-tab-${tab.value}`}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="apercu">{overview}</TabsContent>
      {hasModule('maintenance') && (
        <TabsContent value="documents">
          <TruckDocumentsPanel truck={truck} />
        </TabsContent>
      )}
      {hasModule('trips') && (
        <TabsContent value="trajets">
          <TruckTripsTable truck={truck} />
        </TabsContent>
      )}
      {hasModule('maintenance') && (
        <TabsContent value="maintenance">
          <TruckMaintenancePanel truckId={truck.id} />
        </TabsContent>
      )}
      {hasModule('finance_cockpit') && (
        <TabsContent value="frais">
          <TruckExpensesTab truck={truck} />
        </TabsContent>
      )}
      <TabsContent value="observations">
        <TruckObservationsTab truck={truck} />
      </TabsContent>
      {hasModule('truck_rentals') && (
        <TabsContent value="location">
          <TruckRentalTab truck={truck} />
        </TabsContent>
      )}
      {hasModule('stations') && aDesStations && (
        <TabsContent value="pleins">
          <TruckStationRefuelsTab truck={truck} />
        </TabsContent>
      )}
      {hasModule('finance_cockpit') && canSeeMoney && (
        <TabsContent value="rentabilite">
          <TruckProfitTab truck={truck} />
        </TabsContent>
      )}
    </Tabs>
  );

  return (
    <div
      className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8"
      data-testid="truck-detail-page"
    >
      <PageHeader
        eyebrow="Parc"
        title={truck.matricule || 'Camion'}
        description={
          truck.tank_number
            ? `${LEXIQUE.plaqueCiterne} : ${truck.tank_number}`
            : `${LEXIQUE.plaqueCiterne} non renseignée`
        }
        backTo={backTo}
        backLabel={backLabel}
        meta={
          <span data-testid="truck-status-strip">
            <Badge variant={statusMeta.tone}>{truckStatusLabel(displayStatus)}</Badge>
          </span>
        }
        stats={statsEntete}
        alertesSousTitre
        alertes={alertesEntete}
        actions={
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="truck-edit">
            <Pencil className="h-4 w-4" aria-hidden="true" />
            Modifier
          </Button>
        }
      />

      {/* Sous xl, la colonne latérale passe d'abord: sur un téléphone,
          l'identité et l'immobilisation ne doivent pas tomber sous les
          onglets. */}
      <RecordSplit
        className="[&>aside]:order-first xl:[&>aside]:order-none"
        main={main}
        aside={aside}
      />

      <TruckFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        truck={truck}
        onSaved={refreshAll}
      />
    </div>
  );
}
